import React, { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, AlertCircle, Loader2, ArrowRight } from "lucide-react";

/**
 * VerifyEmail. landing page for the verification link in the welcome email.
 *
 * The link in the email points here with ?token=<32-byte url-safe token>.
 * On mount we POST to /api/auth/verify-email and show success or failure.
 *
 * Three terminal states:
 * - SUCCESS: confirmation + auto-redirect-friendly CTA to Dashboard
 * - EXPIRED / INVALID: clear error + "Resend" CTA for signed-in users
 * - NO TOKEN: explanatory message
 */
export default function VerifyEmail() {
 const [params] = useSearchParams();
 const navigate = useNavigate();
 const { refresh } = useAuth();
 const token = params.get("token");
 const [state, setState] = useState({ status: "checking", message: "" });

 useEffect(() => {
 if (!token) {
 setState({ status: "no-token", message: "This link is missing the verification token. Check the original email and try the link again." });
 return;
 }
 api.post("/auth/verify-email", { token })
.then(({ data }) => {
 setState({ status: "verified", message: `Email verified. welcome aboard, ${data.email}.` });
 // Refresh the user object in AuthContext so the rest of the app sees verified=true immediately
 refresh?.();
 })
.catch(err => {
 setState({
 status: "error",
 message: err.response?.data?.detail || "We couldn't verify that link. It may have expired or already been used.",
 });
 });
 }, [token, refresh]);

 return (
 <div className="min-h-screen paper-grain" data-testid="verify-email-page">
 <Navbar />
 <div className="max-w-2xl mx-auto px-6 sm:px-8 py-20 text-center">
 {state.status === "checking" && (
 <>
 <Loader2 size={28} strokeWidth={1.5} className="mx-auto text-[#1B6C62] animate-spin mb-3" />
 <p className="text-[#6B7280] font-serif">Verifying your email…</p>
 </>
 )}

 {state.status === "verified" && (
 <div data-testid="verify-success">
 <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#E6F5F2] border border-[#1B6C62]/30">
 <CheckCircle2 size={28} strokeWidth={1.5} className="text-[#1B6C62]" />
 </div>
 <div className="label-mono mb-3 text-[#1B6C62]">Email verified ✓</div>
 <h1 className="font-serif text-4xl text-[#0D2B52] mb-3">You&apos;re all set</h1>
 <p className="text-[#333] font-serif leading-relaxed mb-8 max-w-md mx-auto">{state.message}</p>
 <button onClick={() => navigate("/dashboard")} className="btn-primary inline-flex items-center gap-2" data-testid="verify-go-dashboard">
 Go to your dashboard <ArrowRight size={14} strokeWidth={1.5} />
 </button>
 </div>
 )}

 {(state.status === "error" || state.status === "no-token") && (
 <div data-testid="verify-error">
 <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#FFF1E8] border border-[#B35D4C]/30">
 <AlertCircle size={28} strokeWidth={1.5} className="text-[#B35D4C]" />
 </div>
 <div className="label-mono mb-3 text-[#B35D4C]">Verification failed</div>
 <h1 className="font-serif text-4xl text-[#0D2B52] mb-3">That link isn&apos;t valid</h1>
 <p className="text-[#333] font-serif leading-relaxed mb-8 max-w-md mx-auto">{state.message}</p>
 <div className="flex flex-wrap justify-center gap-3">
 <Link to="/account" className="btn-primary inline-flex items-center gap-2" data-testid="verify-go-account">
 Go to Account · resend link
 </Link>
 <Link to="/login" className="btn-secondary inline-flex items-center gap-2" data-testid="verify-go-login">
 Sign in
 </Link>
 </div>
 </div>
 )}
 </div>
 </div>
 );
}
